import React, { useState } from 'react';
import {
  Typography, Card, Table, Tag, Button, Space, Tabs, Tooltip, Statistic, Row, Col,
} from 'antd';
import {
  ReloadOutlined, TrophyOutlined, RiseOutlined, FallOutlined, MinusOutlined,
  ShoppingOutlined, DollarOutlined, ShopOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/api';

const { Title, Text } = Typography;

interface RankItem {
  bot_id: number;
  bot_name: string;
  bot_username?: string;
  is_active: boolean;
  owner?: string;
  orders: number;
  sales: number;
  change_pct?: number | null;
  prev_sales?: number;
  first_order_at?: string | null;
  is_new?: boolean;
  last_order_at?: string | null;
}

const rankBadge = (index: number) => {
  if (index === 0) return <Tag color="gold">🥇 1</Tag>;
  if (index === 1) return <Tag color="silver" style={{ color: '#666' }}>🥈 2</Tag>;
  if (index === 2) return <Tag color="bronze" style={{ color: '#8c5a2b' }}>🥉 3</Tag>;
  return <Tag>{index + 1}</Tag>;
};

const changeRender = (pct?: number | null) => {
  if (pct === null || pct === undefined) return <Text type="secondary">-</Text>;
  if (pct > 0) return <Text style={{ color: '#52c41a' }}><RiseOutlined /> +{pct}%</Text>;
  if (pct < 0) return <Text style={{ color: '#ff4d4f' }}><FallOutlined /> {pct}%</Text>;
  return <Text type="secondary"><MinusOutlined /> 0%</Text>;
};

const Ranking: React.FC = () => {
  const [period, setPeriod] = useState<string>('day');
  const [tab, setTab] = useState<string>('top');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ranking', period, tab],
    queryFn: async () => {
      const res = await analyticsApi.getRanking({ period, type: tab, limit: 30 });
      return (res as any) || [];
    },
  });

  const items: RankItem[] = data || [];
  const totalSales = items.reduce((s, it) => s + (it.sales || 0), 0);
  const totalOrders = items.reduce((s, it) => s + (it.orders || 0), 0);
  const newCount = items.filter((it) => it.is_new).length;

  const columns: any[] = [
    {
      title: '排名', key: 'rank', width: 70,
      render: (_: any, __: any, idx: number) => rankBadge(idx),
    },
    {
      title: '店铺', key: 'shop', width: 220,
      render: (_: any, r: RankItem) => (
        <div>
          <Text strong>{r.bot_name}</Text>
          {r.bot_username && <div><Text type="secondary" style={{ fontSize: 12 }}>@{r.bot_username}</Text></div>}
        </div>
      ),
    },
    {
      title: '销售额(¥)', dataIndex: 'sales', key: 'sales', width: 130, sorter: (a: RankItem, b: RankItem) => a.sales - b.sales,
      render: (v: number, r: RankItem) => <Text strong style={{ color: '#722ed1' }}>¥{v?.toFixed(2)}</Text>,
    },
    {
      title: '订单数', dataIndex: 'orders', key: 'orders', width: 90, sorter: (a: RankItem, b: RankItem) => a.orders - b.orders,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: '客单价', key: 'avg', width: 100,
      render: (_: any, r: RankItem) => r.orders > 0 ? <Text>¥{(r.sales / r.orders).toFixed(2)}</Text> : <Text>-</Text>,
    },
    {
      title: '环比上期', dataIndex: 'change_pct', key: 'change_pct', width: 110,
      render: (v: number | null | undefined) => changeRender(v),
    },
    {
      title: '状态', key: 'status', width: 110,
      render: (_: any, r: RankItem) => (
        <Space>
          {r.is_active ? <Tag color="green">营业中</Tag> : <Tag color="red">已停用</Tag>}
          {r.is_new && <Tag color="blue">新店</Tag>}
        </Space>
      ),
    },
    {
      title: '商户', dataIndex: 'owner', key: 'owner', width: 130,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v || '-'}</Text>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">📊 商家销量排行</Title>
          <Text type="secondary">按 COMPLETED 订单统计，监控头部商家与新商家表现</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>刷新</Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="榜单销售总额" value={totalSales} precision={2} prefix="¥"
              valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="榜单订单数" value={totalOrders}
              prefix={<ShoppingOutlined style={{ color: '#1890ff' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="新店数" value={newCount}
              prefix={<ShopOutlined style={{ color: '#52c41a' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="榜单店铺数" value={items.length}
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />} />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <div className="mb-3 flex items-center justify-between">
          <Tabs
            activeKey={tab}
            onChange={(k) => setTab(k)}
            items={[
              { key: 'top', label: '🏆 头部商家榜' },
              { key: 'new', label: '🆕 新商家榜' },
            ]}
          />
          <Space>
            <span>统计周期：</span>
            <Tabs
              activeKey={period}
              onChange={(k) => setPeriod(k)}
              size="small"
              items={[
                { key: 'day', label: '今日' },
                { key: 'week', label: '本周' },
                { key: 'month', label: '本月' },
              ]}
            />
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="bot_id"
          loading={isLoading}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 30, showSizeChanger: false, showTotal: (t) => `共 ${t} 家店铺` }}
        />
      </Card>
    </div>
  );
};

export default Ranking;
